import { Component, Input, forwardRef } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';

interface TimeOption { value: string; label: string; }

/** Build 30-minute options for a whole day: value 'HH:mm' (24h), label 'h:mm AM/PM'. */
function buildTimeOptions(): TimeOption[] {
  const out: TimeOption[] = [];
  for (let mins = 0; mins < 24 * 60; mins += 30) {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    out.push({ value: pad2(h) + ':' + pad2(m), label: label12(pad2(h) + ':' + pad2(m)) });
  }
  return out;
}
function pad2(n: number): string { return String(n).padStart(2, '0'); }

/** 'HH:mm' (24h) → 'h:mm AM/PM'. */
function label12(v: string): string {
  const [hh, mm] = v.split(':').map(Number);
  const mer = hh < 12 ? 'AM' : 'PM';
  let h = hh % 12; if (h === 0) h = 12;
  return `${h}:${pad2(mm)} ${mer}`;
}

/**
 * Parse a free-typed time into canonical 'HH:mm' (24h), or null if unparseable.
 * Accepts "2:30 pm", "14:30", "2pm", "9", "930", "0930", etc.
 */
function parseTime(raw: string): string | null {
  let s = (raw ?? '').trim().toLowerCase();
  if (!s) return null;
  const mer: 'am' | 'pm' | null = /p/.test(s) ? 'pm' : (/a/.test(s) ? 'am' : null);
  s = s.replace(/[^0-9:]/g, '');
  if (!s) return null;

  let h: number, m: number;
  if (s.includes(':')) {
    const [hh, mm = ''] = s.split(':');
    h = parseInt(hh, 10);
    m = mm === '' ? 0 : parseInt(mm, 10);
  } else if (s.length <= 2) {
    h = parseInt(s, 10); m = 0;
  } else {
    const d = s.padStart(4, '0');
    h = parseInt(d.slice(0, d.length - 2), 10);
    m = parseInt(d.slice(-2), 10);
  }
  if (isNaN(h) || isNaN(m)) return null;
  if (mer === 'pm' && h < 12) h += 12;
  if (mer === 'am' && h === 12) h = 0;
  if (h > 23 || m > 59) return null;
  return pad2(h) + ':' + pad2(m);
}

/**
 * A time field offering BOTH input methods at once:
 *   1. manual entry — type a time in any common format (2:30 PM, 14:30, 930…)
 *   2. choose from a list — an autocomplete panel of 30-minute options
 * ControlValueAccessor over a canonical 'HH:mm' (24-hour) string, so it drops into
 * ngModel / formControlName forms. Empty input emits null.
 *
 *   <app-time-field label="Time" [(ngModel)]="time"></app-time-field>
 */
@Component({
  selector: 'app-time-field',
  templateUrl: './time-field.component.html',
  styleUrls: ['./time-field.component.css'],
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => TimeFieldComponent), multi: true }],
})
export class TimeFieldComponent implements ControlValueAccessor {
  @Input() label = '';
  @Input() width = '100%';
  @Input() placeholder = 'e.g. 2:30 PM';
  @Input() disabled = false;

  readonly options: TimeOption[] = buildTimeOptions();
  filtered: TimeOption[] = this.options;
  ctrl = new FormControl<string>('', { nonNullable: true });
  value: string | null = null;

  private onChangeFn: (v: string | null) => void = () => {};
  onTouched: () => void = () => {};

  onInput(): void {
    const q = (this.ctrl.value ?? '').toLowerCase().replace(/\s+/g, '');
    this.filtered = q
      ? this.options.filter(o => o.label.toLowerCase().replace(/\s+/g, '').includes(q) || o.value.includes(q))
      : this.options;
    // Live-parse so a fully-typed time updates the model without waiting for blur.
    const parsed = parseTime(this.ctrl.value ?? '');
    this.value = parsed;
    this.onChangeFn(this.value);
  }

  onSelect(v: string): void {
    this.value = v;
    this.ctrl.setValue(label12(v), { emitEvent: false });
    this.onChangeFn(this.value);
  }

  onBlur(): void {
    this.onTouched();
    const text = this.ctrl.value ?? '';
    const parsed = parseTime(text);
    if (parsed) {
      this.value = parsed;
      this.ctrl.setValue(label12(parsed), { emitEvent: false });
    } else if (!text.trim()) {
      this.value = null;
    } else {
      this.ctrl.setValue(this.value ? label12(this.value) : '', { emitEvent: false });
    }
    this.filtered = this.options;
    this.onChangeFn(this.value);
  }

  writeValue(v: string | null): void {
    const parsed = v ? parseTime(v) : null;
    this.value = parsed;
    this.ctrl.setValue(parsed ? label12(parsed) : '', { emitEvent: false });
  }
  registerOnChange(fn: (v: string | null) => void): void { this.onChangeFn = fn; }
  registerOnTouched(fn: () => void): void { this.onTouched = fn; }
  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    isDisabled ? this.ctrl.disable({ emitEvent: false }) : this.ctrl.enable({ emitEvent: false });
  }
}
